import { ConnectedPosition } from '@angular/cdk/overlay';
import { NgClass, NgStyle } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { IconComponent } from '@shared/components/icon/icon.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { ActivitySubType, ActivityType } from '@shared/services/gql.service';

import { BeInlineCategoryDropdownOption } from '../be-inline-category-dropdown.model';

@Component({
  imports: [NgClass, NgStyle, TooltipDirective, IconComponent, ReactiveFormsModule],
  selector: 'aux-be-inline-category-option',
  styleUrls: ['be-inline-category-option.component.scss'],
  templateUrl: 'be-inline-category-option.component.html',
})
export class BeInlineCategoryOptionComponent {
  @Output() addCategoryEmit = new EventEmitter<string>();
  @Input() categories: BeInlineCategoryDropdownOption[] = [];
  @ViewChild('categoryInput') categoryInput!: ElementRef;
  categoryNameControl = new FormControl<string>('');
  @Output() editCategoryEmit = new EventEmitter<string>();
  isAddMode = false;
  isEditMode = false;
  @Input() isSelected = false;
  @Input() option: BeInlineCategoryDropdownOption | undefined;
  @Output() selectOptionEmit = new EventEmitter();
  tooltipPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
  ];

  isAddCategoryBtnVisible(): boolean {
    return this.option ? this.option.indent < 6 && this.isUpdateCategoryBtnVisible() : false;
  }

  private isCategoryNameNotUnique(): boolean {
    return !!this.categories.find(
      (category) =>
        category.type === this.option?.type &&
        category.path === (this.isAddMode ? this.option?.fullPath : this.option?.path) &&
        (this.isAddMode
          ? category.name.toLowerCase() === this.categoryNameControl.value?.toLowerCase()
          : category.name === this.categoryNameControl.value && category.id !== this.option.id)
    );
  }

  isEditCategoryBtnVisible(): boolean {
    return this.option ? this.option.indent !== 1 && this.isUpdateCategoryBtnVisible() : false;
  }

  private isUpdateCategoryBtnVisible(): boolean {
    const disallowedTypes: string[] = [
      ActivityType.ACTIVITY_DISCOUNT,
      ActivityType.ACTIVITY_INVESTIGATOR,
      ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_VISITS,
      ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_INVOICEABLES,
      ActivitySubType.ACTIVITY_INVESTIGATOR_SITE_INVOICEABLES,
    ];

    return (
      !this.isAddMode && !this.isEditMode && !disallowedTypes.includes(this.option?.type || '')
    );
  }

  isUpdateCategoryDisabled(): boolean {
    return (
      !this.categoryNameControl.value ||
      !this.categoryNameControl.value?.trim() ||
      this.isCategoryNameNotUnique()
    );
  }

  offUpdateMode(): void {
    this.isAddMode = false;
    this.isEditMode = false;
    this.categoryNameControl.setValue('');
  }

  onAddMode(): void {
    this.isAddMode = true;
    setTimeout(() => {
      this.categoryInput?.nativeElement.focus();
    });
  }

  onDropdownSelected(): void {
    if (this.option?.disabled) {
      return;
    }

    this.selectOptionEmit.emit();
  }

  onEditMode(): void {
    this.isEditMode = true;
    this.categoryNameControl.setValue(this.option?.name || '');
    setTimeout(() => {
      this.categoryInput?.nativeElement.focus();
    });
  }

  updateCategoryBtnTooltip(): string {
    if (!this.categoryNameControl.value || !this.categoryNameControl.value?.trim()) {
      return `Enter unique category name to ${this.isAddMode ? 'create' : 'rename'}.`;
    }

    if (this.isCategoryNameNotUnique()) {
      return 'Category name must be unique within parent.';
    }

    return '';
  }

  updateCategoryList(): void {
    if (this.isUpdateCategoryDisabled()) {
      return;
    }

    if (this.isEditMode && this.categoryNameControl.value === this.option?.name) {
      this.isEditMode = false;

      return;
    }

    if (this.isAddMode) {
      this.isAddMode = false;
      this.addCategoryEmit.emit(this.categoryNameControl.value || '');
    } else {
      this.isEditMode = false;
      this.editCategoryEmit.emit(this.categoryNameControl.value || '');

      if (this.option) {
        this.option.name = this.categoryNameControl.value || '';
      }
    }

    this.categoryNameControl.setValue('');
  }
}
